import bugsnagClient from "@/lib/bugsnag";
import { getPlatform } from "@/utils/getPlatform";
import axios from "axios";

// Datadog removido neste app. Shim no-op para manter as chamadas existentes inertes.
const datadogRum = {
  addAction: (..._args: any[]) => {},
  addError: (..._args: any[]) => {},
};

import { getTenantOrDefault } from "@/utils/getTenant";
import { showOfflineToast } from "@/utils/showOfflineToast";
import { forceLogout } from "@/utils/authUtils";
import { clearGuestOrderAuth, getGuestOrderAuth } from "@/utils/guestOrderAuth";
import useStrapiStore from "@/store/useStrapi";
import {
  isOperatorLocalhostHost,
  shouldRouteApiThroughProxy,
} from "@/lib/operatorDomains";

export const baseUrlApi = process.env.NEXT_PUBLIC_BASE_URL_API;
// export const baseUrlApi = "https://passagens.embarca.ai";

// Determine if we should use the Next.js proxy route (for development to avoid CORS)
function shouldUseProxy(): boolean {
  if (typeof window === "undefined") return false;

  const host = window.location.host;

  // WL *.localhost → proxy for X-API-TOKEN even when DISABLE_PROXY=true.
  if (isOperatorLocalhostHost(host)) return true;

  if (process.env.NEXT_PUBLIC_DISABLE_PROXY === "true") return false;

  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("localhost")
  );
}

// Get the base URL - use proxy route in development, direct API in production
function getBaseUrl(): string {
  if (shouldUseProxy()) {
    // Use Next.js API proxy route to avoid CORS issues in development
    return "/";
  }
  return baseUrlApi || "/";
}

function isCustomDomainHost(): boolean {
  if (typeof window === "undefined") return false;
  return shouldRouteApiThroughProxy(window.location.host);
}

async function getTokenSession() {
  try {
    if (typeof window === "undefined") return "";

    let session_id = localStorage.getItem("session_id");

    if (!session_id) {
      session_id = crypto.randomUUID();
      localStorage.setItem("session_id", session_id);
    }
    return session_id;
  } catch (error) {
    return "";
  }
}

// Função assíncrona para obter o token
async function getAuthToken() {
  // Tenant: header `Tenant` (SSR) → cookie → ?whitelabel → "embarca-ai".
  const operator = getTenantOrDefault();

  try {
    const operatorTokens = JSON.parse(
      process.env.NEXT_PUBLIC_OPERATOR_TOKENS || "{}"
    );
    const envToken = operatorTokens[operator];
    if (envToken) return envToken;
  } catch (error) {
    console.error("Erro ao processar tokens dos operadores:", error);
  }

  // Fallback: AuthToken from Strapi v3-web-resources (store).
  const storeToken = useStrapiStore.getState().authToken;
  if (storeToken) return storeToken;

  return null;
}

// Função utilitária para aguardar um tempo (em ms)
function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function configureHeadersWithRetry(maxRetries = 5, delayMs = 300) {
  const tokenSession = await getTokenSession();
  const platform = getPlatform();

  if (platform === "white-label" || platform.endsWith("-white-label")) {
    return {
      Accept: "application/json",
      ContentType: "application/json",
      TOKEN_SESSION: tokenSession,
      XAPIKEY: "123456789",
      "X-API-TOKEN": await getAuthToken(),
    };
  }

  const authToken = await getAuthToken();
  return {
    Accept: "application/json",
    ContentType: "application/json",
    TOKEN_SESSION: tokenSession,
    XAPIKEY: "123456789",
    "X-API-TOKEN": authToken,
  };
}

const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    XAPIKEY: "123456789",
    "ngrok-skip-browser-warning": "true",
  },
});

const GUEST_ORDER_HEADER_TOKEN = "X-ORDER-TOKEN";
const GUEST_ORDER_HEADER_HMAC = "X-ORDER-HMAC";

function normalizeRequestUrl(url?: string): string {
  const safeUrl = String(url || "");
  if (!safeUrl) return "";
  if (/^https?:\/\//i.test(safeUrl)) {
    try {
      const parsed = new URL(safeUrl);
      return `${parsed.pathname}${parsed.search}`;
    } catch {
      return safeUrl;
    }
  }
  return safeUrl;
}

function shouldAttachGuestOrderHeaders(url?: string, method?: string): boolean {
  const safeUrl = normalizeRequestUrl(url);
  const normalizedMethod = String(method || "").toUpperCase();
  if (!safeUrl) return false;

  const paymentsShow =
    /^\/api\/v1\/payments\/[^/]+$/.test(safeUrl) && normalizedMethod === "GET";
  const paymentsComplete3ds =
    /^\/api\/v1\/payments\/[^/]+\/complete_3ds$/.test(safeUrl) &&
    normalizedMethod === "POST";
  const paymentsRegeneratePix =
    /^\/api\/v1\/payments\/[^/]+\/regenerate_pix$/.test(safeUrl) &&
    normalizedMethod === "POST";
  const reservationCancel =
    /^\/api\/v1\/reservations\/[^/]+\/cancel$/.test(safeUrl) &&
    normalizedMethod === "PUT";
  const reservationCancelAll =
    /^\/api\/v1\/reservations\/cancel_all$/.test(safeUrl) &&
    normalizedMethod === "PUT";
  const reservedSeatCancel =
    /^\/api\/v1\/reserved_seats\/[^/]+\/cancel$/.test(safeUrl) &&
    normalizedMethod === "PUT";
  const orderShow =
    /^\/api\/v1\/orders\/[^/]+$/.test(safeUrl) && normalizedMethod === "GET";
  const orderPaymentdata =
    /^\/api\/v1\/orders\/[^/]+\/paymentdata$/.test(safeUrl) &&
    normalizedMethod === "GET";
  const orderIndex =
    /^\/api\/v1\/orders(\?|$)/.test(safeUrl) && normalizedMethod === "GET";

  return (
    paymentsShow ||
    paymentsComplete3ds ||
    paymentsRegeneratePix ||
    reservationCancel ||
    reservationCancelAll ||
    reservedSeatCancel ||
    orderShow ||
    orderPaymentdata ||
    orderIndex
  );
}

function hasHeader(configHeaders: any, key: string): boolean {
  if (!configHeaders) return false;
  if (typeof configHeaders.has === "function") {
    return configHeaders.has(key);
  }
  const direct = configHeaders[key];
  const lower = configHeaders[String(key).toLowerCase()];
  return !!(direct || lower);
}

function setHeader(configHeaders: any, key: string, value: string): void {
  if (!configHeaders) return;
  if (typeof configHeaders.set === "function") {
    configHeaders.set(key, value);
    return;
  }
  configHeaders[key] = value;
}

function extractErrorText(data: any): string {
  if (!data) return "";
  if (typeof data === "string") return data.toLowerCase();
  if (typeof data?.message === "string") return data.message.toLowerCase();
  if (typeof data?.error === "string") return data.error.toLowerCase();
  if (Array.isArray(data?.errors)) {
    return data.errors
      .filter((e: unknown) => typeof e === "string")
      .join(" ")
      .toLowerCase();
  }
  return "";
}

function isTransient401(data: any): boolean {
  const text = extractErrorText(data);
  if (!text) return false;
  return (
    text.includes("timeout") ||
    text.includes("temporar") ||
    text.includes("service unavailable") ||
    text.includes("gateway") ||
    text.includes("rate limit") ||
    text.includes("too many requests")
  );
}

/**
 * Retorna true se a requisição carregava credenciais HMAC de guest.
 * Usar isso para decidir se uma 401 deve limpar as credenciais guest:
 * se a requisição não tinha X-ORDER-HMAC, o 401 não é uma falha de credencial guest.
 */
function requestHadGuestHmacHeaders(config: any): boolean {
  const headers = config?.headers;
  if (!headers) return false;
  const token =
    headers["X-ORDER-TOKEN"] ??
    headers["x-order-token"] ??
    (typeof headers.get === "function"
      ? headers.get("X-ORDER-TOKEN")
      : undefined);
  const hmac =
    headers["X-ORDER-HMAC"] ??
    headers["x-order-hmac"] ??
    (typeof headers.get === "function"
      ? headers.get("X-ORDER-HMAC")
      : undefined);
  return !!(token && hmac);
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // skip bugsnag on server
    if (typeof window === "undefined") return Promise.reject(error);

    const request = error.config;
    const response = error.response;

    if (response?.data?.url) {
      console.groupCollapsed(
        `%c API Proxy Error: [${request.method?.toUpperCase()}] ${response.data.path} `,
        "background: #ff0000; color: #ffffff; font-weight: bold; border-radius: 4px;"
      );
      console.log(
        "%cReal Backend URL:%c",
        "font-weight: bold;",
        "",
        response.data.url
      );
      console.log(
        "%cBackend Message:%c",
        "font-weight: bold;",
        "",
        response.data.message
      );
      console.log(
        "%cFull Error Context:%c",
        "font-weight: bold;",
        "",
        response.data
      );
      console.groupEnd();
    }

    bugsnagClient.notify(error, (event) => {
      event.addMetadata("request", {
        url: request?.url,
        method: request?.method,
        headers: request?.headers,
        data: request?.data,
        params: request?.params,
      });

      if (response) {
        event.addMetadata("response", {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          data: response.data,
          url_real: response.data?.url,
        });
      }
    });

    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // skip datadog on server
    if (typeof window !== "undefined")
      datadogRum.addAction(`API Request Successful - ${response.config.url}`, {
        url: response.config.url,
        method: response.config.method,
        status: response.status,
        responseData: response.data ? response.data : "No response data",
        headers: response.headers,
      });

    return response;
  },
  (error) => {
    const request = error.config;
    const response = error.response;

    if (!response && typeof navigator !== "undefined" && !navigator.onLine) {
      showOfflineToast(
        "Você está sem internet. Verifique sua conexão e tente novamente."
      );

      datadogRum.addError(
        new Error("User is offline - no internet connection"),
        {
          type: "network_offline",
          url: request?.url,
          method: request?.method,
          isOffline: true,
          userAgent: navigator.userAgent,
        }
      );

      datadogRum.addAction("User Offline - Request Blocked", {
        url: request?.url,
        method: request?.method,
        reason: "no_internet_connection",
      });
      return Promise.reject(error);
    }

    const errorUrl = response?.config?.url || request?.url || "Unknown URL";
    const errorType = !response ? "CORS or Network Error" : "API Error";

    datadogRum.addError(new Error(`API Request Failed - ${errorUrl}`), {
      url: request?.url,
      method: request?.method,
      status: response ? response.status : "Unknown",
      errorMessage: error.message,
      errorType,
      responseData: response ? response.data : "No response data",
      headers: request?.headers,
      params: request?.params,
      isCorsError: !response && error.message?.includes("CORS"),
    });

    datadogRum.addAction(`API Request Failed - ${errorUrl}`, {
      url: request?.url,
      method: request?.method,
      status: response ? response.status : "Unknown",
      errorMessage: error.message,
      errorType,
      responseData: response ? response.data : "No response data",
      headers: request?.headers,
      params: request?.params,
      isCorsError: !response && error.message?.includes("CORS"),
    });

    if (response?.status === 401) {
      const authToken =
        typeof window !== "undefined"
          ? localStorage.getItem("@auth_token")
          : null;
      const hasJwt = !!(authToken && authToken !== "null");

      if (
        !hasJwt &&
        shouldAttachGuestOrderHeaders(request?.url, request?.method)
      ) {
        if (isTransient401(response?.data)) {
          return Promise.reject(error);
        }
        // Só limpa credenciais guest se a requisição de fato carregava HMAC.
        // Evita falsos positivos em 401s de outros contextos (ex: cartão de crédito).
        if (requestHadGuestHmacHeaders(request)) {
          clearGuestOrderAuth();
          window.dispatchEvent(new CustomEvent("guestQuickSigninRequired"));
        }
      } else {
        console.warn("Sessão expirada.");
        forceLogout();
      }
    }
    return Promise.reject(error);
  }
);

api.interceptors.request.use(async function (config) {
  try {
    if (typeof window === "undefined") return config;

    // Custom domain → Next proxy so server injects operator X-API-TOKEN.
    if (isCustomDomainHost() && config.baseURL && config.baseURL !== "/") {
      const url = config.url || "";
      config.baseURL = "/";
      config.url = url.startsWith("/") ? url : `/${url}`;
    }

    const auth_token = localStorage.getItem("@auth_token");

    const authToken = await getAuthToken();

    if (!authToken) {
      console.error("Não foi possível obter o AuthToken");
      return config;
    }

    config.headers.TOKEN_SESSION = await getTokenSession();

    if (config.omitAuthToken) {
      const h = config.headers;
      if (
        h &&
        typeof (h as { delete?: (k: string) => void }).delete === "function"
      ) {
        (h as { delete: (k: string) => void }).delete("Authorization");
      } else if (h && typeof h === "object") {
        delete (h as Record<string, unknown>).Authorization;
      }
    } else if (auth_token && auth_token !== "null") {
      // Apenas enviar Authorization se token existir e não for 'null'
      config.headers.Authorization = `Bearer ${auth_token}`;
      clearGuestOrderAuth();
    }

    if (
      (!auth_token || auth_token === "null") &&
      shouldAttachGuestOrderHeaders(config.url, config.method)
    ) {
      const guestOrderAuth = getGuestOrderAuth();
      if (guestOrderAuth?.orderToken && guestOrderAuth?.orderHmac) {
        if (!hasHeader(config.headers, GUEST_ORDER_HEADER_TOKEN)) {
          setHeader(
            config.headers,
            GUEST_ORDER_HEADER_TOKEN,
            guestOrderAuth.orderToken
          );
        }
        if (!hasHeader(config.headers, GUEST_ORDER_HEADER_HMAC)) {
          setHeader(
            config.headers,
            GUEST_ORDER_HEADER_HMAC,
            guestOrderAuth.orderHmac
          );
        }
      }
    }

    config.headers["X-API-TOKEN"] = authToken;

    return config;
  } catch (error) {
    console.error("Erro no interceptor:", error);
    return config;
  }
});

export { api };
